import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { FileText, Filter, Search, Calendar, Download, Share2, SlidersHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import ShareDocumentModal from '../../components/document-viewer/share-document-modal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { format, isToday, isYesterday, differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { useDocumentCategories } from '../../hooks/useDocumentCategories';
import { useTranslation } from 'react-i18next';

interface Category {
  id: string;
  name: string;
}

interface Requestor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface Document {
  id: string;
  title: string;
  category_id: string;
  category?: Category;
  requestor?: Requestor;
}

interface SigningRequest {
  id: string;
  document_id: string;
  document: Document;
  signatory_id: string;
  requestor_id: string;
  status: string;
  created_at: string;
  signed_at: string | null;
  declined_at: string | null;
  signing_order: number;
}

const SigningRequests: React.FC = () => {
  const { t } = useTranslation(['signing', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Share modal state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [documentToShare, setDocumentToShare] = useState<{ id: string; title: string } | null>(null);
  
  // Parse URL parameters
  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search);
    return {
      timeFilter: params.get('timeFilter') || 'month',
      categoryFilter: params.get('categoryId') || 'all',
      statusFilter: params.get('status') || 'pending',
      searchTerm: params.get('search') || '',
    };
  };
  
  // State for signing requests data
  const [signingRequests, setSigningRequests] = useState<SigningRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState<{ current_page: number; last_page: number; per_page: number; total: number } | null>(null);
  const [timeFilter, setTimeFilter] = useState<string>(getInitialFilters().timeFilter);
  const [categoryFilter, setCategoryFilter] = useState<string>(getInitialFilters().categoryFilter);
  const [statusFilter, setStatusFilter] = useState<string>(getInitialFilters().statusFilter);
  const [searchTerm, setSearchTerm] = useState<string>(getInitialFilters().searchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(getInitialFilters().searchTerm);
  
  // Use our custom hook for categories
  const { categories: fetchedCategories } = useDocumentCategories();
  
  // Extract categories array from the response if needed
  const categoriesArray = Array.isArray(fetchedCategories) ? fetchedCategories : 
                         (fetchedCategories && 'categories' in fetchedCategories) ? 
                         (fetchedCategories as any).categories : [];

  // Debounce search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update URL when filters or search change
  useEffect(() => {
    const params = new URLSearchParams();
    if (timeFilter !== 'month') params.set('timeFilter', timeFilter);
    if (categoryFilter !== 'all') params.set('categoryId', categoryFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
    
    // Update URL without causing a navigation/reload
    navigate({ search: params.toString() }, { replace: true });
    
    // Fetch signing requests with the new filters
    fetchSigningRequests();
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm, currentPage]);

  // Fetch signing requests data with the latest filters
  const fetchSigningRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get API endpoint
      const endpoint = '/api/signing-requests';
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (statusFilter !== 'all') queryParams.set('status', statusFilter);
      if (categoryFilter !== 'all') queryParams.set('category_id', categoryFilter);
      if (timeFilter !== 'all') queryParams.set('time_period', timeFilter);
      if (debouncedSearchTerm.trim().length >= 3) queryParams.set('search', debouncedSearchTerm.trim());
      queryParams.set('page', currentPage.toString());
      
      // Add special parameter to filter by current user
      queryParams.set('current_user_only', 'true');
      
      // Use fetch with full URL to ensure proper connection
      // Using centralized API configuration
      const apiUrl = API_BASE_URL.replace(/\/api$/, '');
      
      // Add logging to help debug the API request
      const requestUrl = `${apiUrl}${endpoint}?${queryParams.toString()}`;
      console.log('Making API request to:', requestUrl);
      
      const response = await fetch(
        requestUrl, 
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Signing Requests API response:', data);
      
      if (data) {
        // Laravel pagination returns data in a 'data' property
        setSigningRequests(data.data || []);
        setPaginationMeta({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        });
      }
    } catch (err: any) {
      console.error('Error fetching signing requests:', err);
      setError(err.message || 'Failed to fetch signing requests data');
    } finally {
      setLoading(false);
    }
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm]);

  // Initial data fetch
  useEffect(() => {
    fetchSigningRequests();
  }, []);
  
  // Document statuses for filter dropdown - match signing request statuses
  const signingRequestStatuses = [
    { value: 'all', label: t('common:status.all') },
    { value: 'pending', label: t('common:status.pending') },
    { value: 'signed', label: t('common:status.signed') },
    { value: 'declined', label: t('common:status.declined') }
  ];

  // Time periods for filter dropdown
  const timePeriods = [
    { value: 'week', label: t('common:time.this_week') },
    { value: 'month', label: t('common:time.this_month') },
    { value: 'quarter', label: t('common:time.this_quarter') },
    { value: 'year', label: t('common:time.this_year') },
    { value: 'all', label: t('common:time.all_time') }
  ];

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{t('common:status.pending')}</Badge>;
      case 'signed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{t('common:status.signed')}</Badge>;
      case 'declined':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{t('common:status.declined')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Format full date for tooltip
  const formatFullDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (err) {
      return dateString;
    }
  };

  // Format relative date for display
  const formatRelativeDate = (dateString: string | null) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const now = new Date();

      if (isToday(date)) return format(date, 'h:mm a');
      if (isYesterday(date)) return 'Yesterday';

      const daysDiff = differenceInDays(now, date);
      if (daysDiff < 7) return `${daysDiff} days ago`;

      const weeksDiff = differenceInWeeks(now, date);
      if (weeksDiff === 1) return 'Last week';
      if (weeksDiff < 4) return `${weeksDiff} weeks ago`;

      const monthsDiff = differenceInMonths(now, date);
      if (monthsDiff === 1) return 'Last month';
      if (monthsDiff < 12) return `${monthsDiff} months ago`;

      const yearsDiff = differenceInYears(now, date);
      if (yearsDiff === 1) return 'Last year';
      return `${yearsDiff} years ago`;
    } catch (err) {
      return dateString;
    }
  };
  
  // Filtered requests are handled by the API call
  const filteredSigningRequests = signingRequests;

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Page header with filters */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('signing:page_title')}</h1>
          <p className="text-muted-foreground">
            {t('signing:page_subtitle')}
          </p>
        </div>
        
        <div className="flex flex-row gap-3">
          <Button
            variant="outline"
            className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4 text-indigo-500" />
            {showFilters ? t('signing:hide_filters') : t('signing:show_filters')}
          </Button>
        </div>
      </div>
      
      {/* Filters section */}
      {showFilters && <Card className="border-indigo-100 bg-gradient-to-r from-indigo-50/50 via-white to-purple-50/50 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-indigo-100">
              <SlidersHorizontal className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-lg">{t('signing:filters_title')}</CardTitle>
              <CardDescription>{t('signing:filters_description')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Time Period filter */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-amber-700">
                <Calendar className="h-3.5 w-3.5" />
                {t('signing:time_period')}
              </label>
              <Select value={timeFilter} onValueChange={setTimeFilter}>
                <SelectTrigger className="bg-white border-amber-200 focus:ring-amber-300">
                  <SelectValue placeholder={t('signing:select_period')} />
                </SelectTrigger>
                <SelectContent>
                  {timePeriods.map(period => (
                    <SelectItem key={period.value} value={period.value}>
                      {period.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Category filter */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-blue-700">
                <FileText className="h-3.5 w-3.5" />
                {t('signing:category')}
              </label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="bg-white border-blue-200 focus:ring-blue-300">
                  <SelectValue placeholder={t('signing:select_category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('signing:all_categories')}</SelectItem>
                  {categoriesArray.map((category: {id: string, name: string}) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status filter */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-purple-700">
                <Filter className="h-3.5 w-3.5" />
                {t('signing:status')}
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-white border-purple-200 focus:ring-purple-300">
                  <SelectValue placeholder={t('signing:select_status')} />
                </SelectTrigger>
                <SelectContent>
                  {signingRequestStatuses.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Search */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-emerald-700">
                <Search className="h-3.5 w-3.5" />
                {t('signing:search')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400" />
                <Input
                  placeholder={t('signing:search_placeholder')} 
                  className="pl-9 bg-white border-emerald-200 focus:ring-emerald-300"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>}
      
      {/* Signing Requests table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <p>{error}</p>
              <Button 
                variant="outline" 
                onClick={fetchSigningRequests} 
                className="mt-4"
              >
                {t('common:actions.try_again')}
              </Button>
            </div>
          ) : filteredSigningRequests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('signing:no_requests_title')}</h3>
              <p className="text-muted-foreground mb-6">
                {t('signing:no_requests_message')}
              </p>
              {!showFilters && (
                <Button
                  variant="outline"
                  className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  onClick={() => setShowFilters(true)}
                >
                  <SlidersHorizontal className="mr-2 h-4 w-4 text-indigo-500" />
                  {t('signing:show_filters')}
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('signing:table_document_title')}</TableHead>
                    <TableHead>{t('signing:table_category')}</TableHead>
                    <TableHead>{t('signing:table_requestor')}</TableHead>
                    <TableHead>{t('signing:table_status')}</TableHead>
                    <TableHead>{t('signing:table_sent')}</TableHead>
                    <TableHead>{t('signing:table_signed_date')}</TableHead>
                    <TableHead>{t('signing:table_declined_date')}</TableHead>
                    <TableHead className="text-right">{t('signing:table_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSigningRequests.map((request) => (
                    <TableRow 
                      key={request.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/document-viewer/${request.document.id}`)}
                    >
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {request.document.title}
                      </TableCell>
                      <TableCell>
                        {request.document.category?.name || t('signing:uncategorized')}
                      </TableCell>
                      <TableCell>
                        {request.document.requestor?.first_name} {request.document.requestor?.last_name}
                      </TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell className="whitespace-nowrap" title={request.created_at ? formatFullDate(request.created_at) : ''}>{formatRelativeDate(request.created_at)}</TableCell>
                      <TableCell className="whitespace-nowrap" title={request.signed_at ? formatFullDate(request.signed_at) : ''}>{formatRelativeDate(request.signed_at)}</TableCell>
                      <TableCell className="whitespace-nowrap" title={request.declined_at ? formatFullDate(request.declined_at) : ''}>{formatRelativeDate(request.declined_at)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 hover:bg-green-50"
                            title="Download"
                            onClick={async () => {
                              try {
                                const response = await fetch(`${API_BASE_URL}/documents/${request.document.id}/stream`, {
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                  },
                                });
                                if (!response.ok) {
                                  if (response.status === 401) { handleUnauthorized(); return; }
                                  throw new Error('Download failed');
                                }
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = window.document.createElement('a');
                                a.href = url;
                                a.download = `${request.document.title}.pdf`;
                                window.document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                a.remove();
                              } catch (err) {
                                console.error('Download error:', err);
                              }
                            }}
                          >
                            <Download className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-purple-50"
                            title="Share"
                            onClick={() => {
                              setDocumentToShare({ id: request.document.id, title: request.document.title });
                              setShareDialogOpen(true);
                            }}
                          >
                            <Share2 className="h-4 w-4 text-purple-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {paginationMeta && paginationMeta.last_page > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t('common:pagination.showing', {
                  start: ((paginationMeta.current_page - 1) * paginationMeta.per_page) + 1,
                  end: Math.min(paginationMeta.current_page * paginationMeta.per_page, paginationMeta.total),
                  total: paginationMeta.total
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('common:pagination.previous')}
                </Button>
                <span className="text-sm font-medium">
                  {t('common:pagination.page_of', { current: paginationMeta.current_page, last: paginationMeta.last_page })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= paginationMeta.last_page}
                  onClick={() => setCurrentPage((p) => Math.min(paginationMeta.last_page, p + 1))}
                >
                  {t('common:pagination.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Share document modal */}
      {documentToShare && (
        <ShareDocumentModal
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          documentId={documentToShare.id}
          documentName={documentToShare.title}
        />
      )}
    </div>
  );
};

export default SigningRequests;
